import { Link, redirect, useFetcher, useLoaderData, type LoaderFunctionArgs } from 'react-router';
import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  Label,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  toast,
} from '@repo-manager/ui';

import { useRepos } from '../lib/repo-context';
import { getSupabase } from '../lib/supabase';

interface FeatureRow {
  id: string;
  title: string;
  narrative: string;
  success_criteria: string | null;
  status: string;
  priority: number;
  milestone_id: string | null;
  repo_id: string;
}

interface EpicRow {
  id: string;
  title: string;
  status: string;
  rationale: string;
}

interface ReqRow {
  id: string;
  type: string;
  description: string;
  active: boolean;
}

interface LoaderData {
  feature: FeatureRow;
  epics: EpicRow[];
  requirements: ReqRow[];
}

export async function featureLoader({ params }: LoaderFunctionArgs): Promise<LoaderData> {
  if (!params.id) throw new Response('Not Found', { status: 404 });
  const supabase = getSupabase();
  const { data: feature, error } = await supabase
    .from('features')
    .select('id, title, narrative, success_criteria, status, priority, milestone_id, repo_id')
    .eq('id', params.id)
    .single();
  if (error || !feature) throw new Response('Feature not found', { status: 404 });
  const [epicsR, reqsR] = await Promise.all([
    supabase
      .from('epics')
      .select('id, title, status, rationale')
      .eq('feature_id', feature.id)
      .order('proposed_at', { ascending: false }),
    supabase
      .from('requirements')
      .select('id, type, description, active')
      .eq('repo_id', feature.repo_id),
  ]);
  return {
    feature,
    epics: epicsR.data ?? [],
    requirements: reqsR.data ?? [],
  };
}

export async function featureAction({ params, request }: LoaderFunctionArgs) {
  if (!params.id) return null;
  const supabase = getSupabase();
  const fd = await request.formData();
  const op = fd.get('op');
  if (op === 'update') {
    const patch = {
      title: String(fd.get('title') ?? '').trim(),
      narrative: String(fd.get('narrative') ?? '').trim(),
      success_criteria: (fd.get('success_criteria') as string | null) || null,
      priority: Number(fd.get('priority') ?? 0),
    };
    const { error } = await supabase.from('features').update(patch).eq('id', params.id);
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  }
  if (op === 'delete') {
    const { error } = await supabase.from('features').delete().eq('id', params.id);
    if (error) return { ok: false, error: error.message };
    return redirect('/roadmap');
  }
  return null;
}

export function FeaturePage() {
  const { feature, epics, requirements } = useLoaderData() as LoaderData;
  const { repos } = useRepos();
  const repo = repos.find((r) => r.id === feature.repo_id) ?? null;
  const fetcher = useFetcher();
  const [draft, setDraft] = useState({
    title: feature.title,
    narrative: feature.narrative,
    success_criteria: feature.success_criteria ?? '',
    priority: feature.priority,
  });

  useEffect(() => {
    const r = fetcher.data;
    if (r && typeof r === 'object' && 'ok' in r) {
      if ((r as { ok: boolean }).ok) toast.success('Feature updated');
      else toast.error('Update failed', { description: (r as { error?: string }).error });
    }
  }, [fetcher.data]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          {repo ? (
            <p className="text-sm font-mono text-muted-foreground">
              {repo.owner}/{repo.name}
            </p>
          ) : null}
          <h1 className="text-2xl font-semibold tracking-tight">{feature.title}</h1>
          <div className="mt-1 flex items-center gap-2">
            <StatusBadge status={feature.status} />
            <span className="text-sm text-muted-foreground">priority {feature.priority}</span>
          </div>
        </div>
        <Button asChild variant="ghost">
          <Link to="/roadmap">← Roadmap</Link>
        </Button>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="epics">Epics ({epics.length})</TabsTrigger>
          <TabsTrigger value="requirements">Requirements ({requirements.length})</TabsTrigger>
          <TabsTrigger value="edit">Edit</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Narrative</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap text-sm">{feature.narrative}</p>
              {feature.success_criteria ? (
                <>
                  <h3 className="mt-6 text-sm font-medium">Success criteria</h3>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
                    {feature.success_criteria}
                  </p>
                </>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="epics">
          {epics.length === 0 ? (
            <EmptyState
              title="No epics yet"
              description="The manager DO will propose epics when this feature is approved."
            />
          ) : (
            <div className="space-y-3">
              {epics.map((e) => (
                <Card key={e.id}>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between text-base">
                      <Link to={`/epics/${e.id}`} className="hover:underline">
                        {e.title}
                      </Link>
                      <StatusBadge status={e.status} />
                    </CardTitle>
                    <CardDescription className="line-clamp-2">{e.rationale}</CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="requirements">
          {requirements.length === 0 ? (
            <EmptyState title="No requirements" description="Requirements live at the repo level." />
          ) : (
            <Card>
              <CardContent className="p-0">
                <ul className="divide-y divide-border">
                  {requirements.map((r) => (
                    <li key={r.id} className="flex items-center gap-3 px-6 py-3 text-sm">
                      <span className="w-16 text-xs uppercase text-muted-foreground">{r.type}</span>
                      <span className="flex-1">{r.description}</span>
                      {!r.active ? <StatusBadge status="closed" /> : null}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="edit">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Update feature</CardTitle>
              <CardDescription>RLS lets product+project+admin edit.</CardDescription>
            </CardHeader>
            <CardContent>
              <fetcher.Form method="post" className="space-y-4">
                <input type="hidden" name="op" value="update" />
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    name="title"
                    value={draft.title}
                    onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="narrative">Narrative</Label>
                  <Textarea
                    id="narrative"
                    name="narrative"
                    rows={6}
                    value={draft.narrative}
                    onChange={(e) => setDraft((d) => ({ ...d, narrative: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="success_criteria">Success criteria</Label>
                  <Textarea
                    id="success_criteria"
                    name="success_criteria"
                    rows={4}
                    value={draft.success_criteria}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, success_criteria: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="priority">Priority</Label>
                  <Input
                    id="priority"
                    name="priority"
                    type="number"
                    value={draft.priority}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, priority: Number(e.target.value) }))
                    }
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={fetcher.state !== 'idle'}>
                    Save
                  </Button>
                </div>
              </fetcher.Form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
